import React, { useMemo, useRef, useState, useEffect } from "react";
import { CustomIcon } from "../shared/CustomIcon";
import { nodeHealth } from "./NodeCircle";

// =============================================================
// DrilldownView — mobile-first drill-down navigation. Renders a
// stack of "levels" as the user taps deeper, with a smooth
// horizontal slide animation. Includes breadcrumb + My Tree
// shortcut + role-based visibility gating + quick-actions menu.
// =============================================================

const LEVEL_ABBR = [
  "Be","Ga","De","Ep","Ze","Et","Th","Io","Ka","La","Mu",
  "Nu","Xi","Om","Pi","Rh","Si","Ta","Up","Ph","Ch","Ps","Og",
];

const HEALTH_DOT = {
  active: "#4ade80", amber: "#f59e0b", red: "#ff6b6b",
  inactive: "rgba(200,200,200,0.55)", deceased: "#a5b4fc", vacant: "var(--kc-accent)",
};

// Can the current user drill into `node`?
function canAccess(node, currentUser, tree, isSuperAdmin, isLimitedAdmin) {
  if (isSuperAdmin || isLimitedAdmin) return true;
  if (!currentUser?.memberId) return false;
  // Find the user's own node.
  const myNode = Object.values(tree?.nodes || {}).find(n => n.memberId === currentUser.memberId && n.status === "Occupied");
  if (!myNode) return false;
  // If the user is Alpha (level 1) they see the full subtree.
  if (myNode.level === 1) return true;
  // Otherwise the user can access: their own node, their parent (1 up),
  // their 3 children (1 down). Nothing else.
  if (node.nodeId === myNode.nodeId) return true;
  if (node.nodeId === myNode.parentNodeId) return true;
  if ((myNode.childNodeIds || []).includes(node.nodeId)) return true;
  return false;
}

// Node card row used in both the header (large) and child list (compact).
function NodeRow({
  node, member, size, highlighted, restricted, isSuperAdmin, onOpenActions, onClick, isHeader, healthOverride,
}) {
  const health = healthOverride || nodeHealth(node);
  const abbr = (node?.level && node.level >= 1) ? (LEVEL_ABBR[node.level - 1] || "?") : (node?.isGodNode ? "Alpha" : "?");
  const isVacant = !node?.memberId;

  // Special God-node row: crown avatar + "God" name + identity line.
  if (node?.isGodNode) {
    const photoSize = size || 60;
    return (
      <button
        type="button"
        data-testid={`dd-row-god${isHeader ? "-header" : ""}`}
        onClick={onClick || undefined}
        style={{
          appearance: "none", width: "100%", cursor: onClick ? "pointer" : "default",
          display: "flex", alignItems: "center", gap: 12,
          padding: isHeader ? "14px 14px" : "12px 12px",
          background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)",
          border: "1px solid color-mix(in srgb, var(--kc-accent) 50%, transparent)",
          borderRadius: 12, textAlign: "left", color: "inherit", font: "inherit",
          boxShadow: "0 0 24px color-mix(in srgb, var(--kc-accent) 35%, transparent)", transition: "box-shadow 0.15s",
        }}
      >
        <div style={{
          width: photoSize, height: photoSize, borderRadius: "50%",
          background: "radial-gradient(circle at 30% 30%, #F5D77A, #B8860B 65%, #6b4a05)",
          border: "2px solid var(--kc-accent)", flexShrink: 0,
          boxShadow: "0 0 18px color-mix(in srgb, var(--kc-accent) 65%, transparent)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <CustomIcon name="crown" size={Math.round(photoSize * 0.55)} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: isHeader ? 18 : 15, fontWeight: 700, color: "var(--kc-accent)", letterSpacing: "0.10em", textTransform: "uppercase" }}>
            God
          </div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontStyle: "italic", fontSize: 11, color: "var(--kc-sub)", marginTop: 2 }}>
            {node.identity || "Father, Son and Holy Spirit"}
          </div>
        </div>
      </button>
    );
  }

  const displayName = member ? `${member.firstName || ""} ${member.lastName || ""}`.trim() : (isVacant ? "Vacant slot" : "Unknown");
  const photoSize = size || 52;
  return (
    <button
      type="button"
      data-testid={`dd-row-${node?.slotRef}${isHeader ? "-header" : ""}`}
      onClick={!restricted && onClick ? onClick : undefined}
      disabled={restricted}
      style={{
        appearance: "none", width: "100%", cursor: restricted ? "not-allowed" : (onClick ? "pointer" : "default"),
        display: "flex", alignItems: "center", gap: 12,
        padding: isHeader ? "14px 14px" : "12px 12px",
        background: isHeader ? "var(--kc-elevated)" : "#1F1F1F",
        border: isHeader ? "1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent)" : "1px solid rgba(255,255,255,0.06)",
        borderRadius: 12, textAlign: "left", color: "inherit", font: "inherit",
        boxShadow: highlighted ? "0 0 0 2px var(--kc-accent), 0 0 20px color-mix(in srgb, var(--kc-accent) 45%, transparent)" : "none",
        opacity: restricted ? 0.55 : 1, transition: "box-shadow 0.15s, background 0.15s",
      }}
    >
      {/* Avatar */}
      {isVacant ? (
        <div style={{ width: photoSize, height: photoSize, borderRadius: "50%", border: "2px dashed var(--kc-accent)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--kc-accent)", flexShrink: 0 }}>
          <CustomIcon name="plus" size={photoSize / 3} />
        </div>
      ) : member?.photo ? (
        <img src={member.photo} alt="" style={{ width: photoSize, height: photoSize, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
      ) : (
        <div style={{ width: photoSize, height: photoSize, borderRadius: "50%", background: "var(--kc-accent)", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, flexShrink: 0 }}>
          {(member?.firstName?.[0] || "?") + (member?.lastName?.[0] || "")}
        </div>
      )}
      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: isHeader ? 15 : 13, fontWeight: 700, color: isHeader ? "var(--kc-text)" : "#FFFFFF", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {displayName}
        </div>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: "color-mix(in srgb, var(--kc-accent) 85%, transparent)", marginTop: 2 }}>
          {abbr} · #{node?.memberNumber || (isVacant ? node?.slotRef : "—")}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
          {health === "deceased" ? (
            <span style={{ color: HEALTH_DOT.deceased, display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10 }}>
              <CustomIcon name="dove" size={11} /> Deceased
            </span>
          ) : (
            <>
              <span data-testid={`dd-row-dot-${node?.slotRef}`} style={{ width: 8, height: 8, borderRadius: "50%", background: HEALTH_DOT[health], display: "inline-block" }} />
              <span style={{ fontSize: 10, color: "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif" }}>
                {isVacant ? node?.slotRef : (member?.status || "Active")}
              </span>
            </>
          )}
          {node?.lastFollowUpAt && (
            <span style={{ fontSize: 10, color: "var(--kc-meta)", marginLeft: 6 }}>
              Last: {String(node.lastFollowUpAt).slice(0, 10)}
            </span>
          )}
        </div>
        {restricted && (
          <div data-testid={`dd-row-locked-${node?.slotRef}`} style={{ fontSize: 10, color: "#ff6b6b", marginTop: 4, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <CustomIcon name="lock" size={10} /> Access restricted to your branch
          </div>
        )}
      </div>
      {/* Right side: 3-dot menu + chevron */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
        {!isVacant && onOpenActions && (
          <span
            role="button"
            aria-label="Quick actions"
            data-testid={`dd-row-menu-${node?.slotRef}`}
            onClick={(e) => { e.stopPropagation(); onOpenActions(node); }}
            style={{ width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--kc-sub)", cursor: "pointer" }}
          >
            <CustomIcon name="dots-vertical" size={14} />
          </span>
        )}
        {!isHeader && !restricted && (
          <CustomIcon name="arrow-right" size={14} />
        )}
        {restricted && (
          <CustomIcon name="lock" size={13} />
        )}
      </div>
    </button>
  );
}

export default function DrilldownView({
  tree, members, currentUser, isSuperAdmin, isLimitedAdmin, healthMap = {},
  onOpenNode, onOpenActions, initialFocus: initialFocusOverride,
}) {
  const nodes = tree?.nodes || {};
  const rootId = tree?.godNodeId || tree?.alphaNodeId;

  // Build a stack from root down to the given target node.
  const buildPath = (targetId) => {
    if (!targetId || !nodes[targetId]) return rootId ? [rootId] : [];
    const path = [];
    let cur = nodes[targetId];
    while (cur) {
      path.unshift(cur.nodeId);
      cur = cur.parentNodeId ? nodes[cur.parentNodeId] : null;
    }
    return path;
  };

  // Determine initial focus for role-scoped users. Admins/Alphas start at root (God);
  // other users start at their own node. Parent may pass initialFocusOverride
  // (e.g. "My Tree" click) which takes precedence.
  const initialFocus = useMemo(() => {
    if (initialFocusOverride) return initialFocusOverride;
    if (isSuperAdmin || isLimitedAdmin) return rootId;
    if (!currentUser?.memberId) return rootId;
    const mine = Object.values(nodes).find(n => n.memberId === currentUser.memberId && n.status === "Occupied");
    if (!mine) return rootId;
    if (mine.level <= 1) return rootId; // Alphas start at God so they see peers
    return mine.nodeId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootId, isSuperAdmin, isLimitedAdmin, currentUser?.memberId, initialFocusOverride]);

  const [stack, setStack] = useState(() => buildPath(initialFocus));
  const [direction, setDirection] = useState("forward");
  const containerRef = useRef(null);

  // If parent updates initialFocusOverride (e.g. My Tree click while already in
  // drilldown mode), rebuild the stack to the new target.
  useEffect(() => {
    if (initialFocusOverride) {
      const path = buildPath(initialFocusOverride);
      setDirection("forward");
      setStack(path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFocusOverride]);

  useEffect(() => {
    if (initialFocus && stack.length === 0) setStack(buildPath(initialFocus));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFocus, stack.length]);

  const focusId = stack[stack.length - 1];
  const focusNode = focusId ? nodes[focusId] : null;
  const parent = focusNode?.parentNodeId ? nodes[focusNode.parentNodeId] : null;
  const children = focusNode ? (focusNode.childNodeIds || []).map(id => nodes[id]).filter(Boolean) : [];
  // Pad to 3 children with placeholders.
  while (children.length < 3) {
    children.push({
      nodeId: `__ph_${focusId || "root"}_${children.length}`,
      slotRef: null, level: (focusNode?.level ?? -1) + 1, levelName: "",
      status: "Vacant", memberId: null, childNodeIds: [], _placeholder: true,
    });
  }

  // Breadcrumb: chain from root of stack down to focus.
  const breadcrumb = useMemo(() => stack.map(id => nodes[id]).filter(Boolean), [stack, nodes]);

  const goDeeper = (child) => {
    if (child._placeholder) return;
    if (!canAccess(child, currentUser, tree, isSuperAdmin, isLimitedAdmin)) return;
    setDirection("forward");
    setStack(prev => [...prev, child.nodeId]);
  };
  const goBack = () => {
    if (stack.length <= 1) return;
    setDirection("back");
    setStack(prev => prev.slice(0, -1));
  };
  const jumpToCrumb = (idx) => {
    if (idx >= stack.length - 1) return;
    setDirection("back");
    setStack(prev => prev.slice(0, idx + 1));
  };
  const jumpToMine = () => {
    if (!currentUser?.memberId) return;
    const mine = Object.values(nodes).find(n => n.memberId === currentUser.memberId && n.status === "Occupied");
    if (!mine) return;
    // Build a stack from root down to `mine` for a natural breadcrumb.
    const path = [];
    let cur = mine;
    while (cur) { path.unshift(cur.nodeId); cur = cur.parentNodeId ? nodes[cur.parentNodeId] : null; }
    setDirection("forward");
    setStack(path);
  };

  const [anim, setAnim] = useState(0);
  useEffect(() => { setAnim(a => a + 1); }, [focusId]);

  const userNodeId = currentUser?.memberId
    ? (Object.values(nodes).find(n => n.memberId === currentUser.memberId && n.status === "Occupied") || {}).nodeId
    : null;

  return (
    <div data-testid="drilldown-view" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Header: back + breadcrumb + My Tree */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          data-testid="dd-back"
          onClick={goBack}
          disabled={stack.length <= 1}
          aria-label="Back"
          style={{ background: "transparent", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", color: "var(--kc-accent)", borderRadius: 999, width: 34, height: 34, cursor: stack.length <= 1 ? "not-allowed" : "pointer", opacity: stack.length <= 1 ? 0.45 : 1, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}
        >←</button>
        <div data-testid="dd-breadcrumb" style={{ flex: 1, overflowX: "auto", whiteSpace: "nowrap", scrollbarWidth: "none" }}>
          {breadcrumb.map((n, i) => (
            <React.Fragment key={n.nodeId}>
              <button
                type="button"
                data-testid={`dd-crumb-${n.slotRef}`}
                onClick={() => jumpToCrumb(i)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 6px", color: i === breadcrumb.length - 1 ? "var(--kc-accent)" : "var(--kc-sub)", fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: i === breadcrumb.length - 1 ? 700 : 400, textDecoration: i === breadcrumb.length - 1 ? "none" : "underline dashed rgba(255,255,255,0.25)" }}
              >
                {n.levelName || "?"}{n.memberNumber ? ` (#${n.memberNumber})` : ""}
              </button>
              {i < breadcrumb.length - 1 && <span style={{ color: "var(--kc-meta)" }}>→</span>}
            </React.Fragment>
          ))}
        </div>
        {currentUser?.memberId && userNodeId && (
          <button
            type="button"
            data-testid="dd-my-tree"
            onClick={jumpToMine}
            className="btn btn-sm btn-gold"
            style={{ whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6 }}
          ><CustomIcon name="celebrate" size={12} /> My Tree</button>
        )}
      </div>

      {/* Slide-animated frame */}
      <div ref={containerRef} style={{ overflow: "hidden", borderRadius: 12 }}>
        <div
          key={anim}
          data-testid="dd-frame"
          style={{
            display: "flex", flexDirection: "column", gap: 10,
            animation: `${direction === "forward" ? "ddSlideInFromRight" : "ddSlideInFromLeft"} 0.25s ease-in-out`,
          }}
        >
          <style>{`
            @keyframes ddSlideInFromRight { from { transform: translateX(24%); opacity: 0.5; } to { transform: translateX(0); opacity: 1; } }
            @keyframes ddSlideInFromLeft  { from { transform: translateX(-24%); opacity: 0.5; } to { transform: translateX(0); opacity: 1; } }
          `}</style>

          {/* Focus card (large header) */}
          {focusNode && (
            <NodeRow
              node={focusNode}
              member={focusNode.memberId ? (members || []).find(m => m.id === focusNode.memberId) : null}
              size={64}
              isHeader
              highlighted={focusNode.nodeId === userNodeId}
              restricted={false}
              isSuperAdmin={isSuperAdmin}
              healthOverride={healthMap[focusNode.nodeId]?.health}
              onClick={() => onOpenNode && onOpenNode(focusNode)}
              onOpenActions={onOpenActions}
            />
          )}

          {/* Parent context (small, one level above focus) */}
          {parent && (
            <div data-testid="dd-parent-context" style={{ padding: "4px 4px 0 4px", fontSize: 10, color: "var(--kc-meta)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>
              Leader (one up): <button type="button" onClick={() => jumpToCrumb(stack.length - 2)} data-testid={`dd-parent-crumb-${parent.slotRef}`} style={{ background: "none", border: "none", color: "var(--kc-accent)", cursor: "pointer", padding: 0, fontFamily: "inherit", fontSize: "inherit", textDecoration: "underline dashed" }}>
                {parent.levelName} · {parent.slotRef}
              </button>
            </div>
          )}

          {/* Direct reports (children) — always show 3 slots */}
          <div data-testid="dd-children" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 10, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.10em", fontWeight: 700, padding: "8px 4px 0 4px" }}>
              Direct reports
            </div>
            {children.map((c) => {
              const m = c.memberId ? (members || []).find(x => x.id === c.memberId) : null;
              const restricted = !c._placeholder && !canAccess(c, currentUser, tree, isSuperAdmin, isLimitedAdmin);
              return (
                <NodeRow
                  key={c.nodeId}
                  node={c}
                  member={m}
                  size={52}
                  highlighted={c.nodeId === userNodeId}
                  restricted={restricted}
                  isSuperAdmin={isSuperAdmin}
                  healthOverride={healthMap[c.nodeId]?.health}
                  onClick={() => (c._placeholder || restricted) ? onOpenNode?.(c) : goDeeper(c)}
                  onOpenActions={restricted || c._placeholder ? null : onOpenActions}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
